<script lang="ts" setup>
import { faStar } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import type { TemplateObject } from "~/components/automation/TemplateSelector.vue";

defineProps<{
    template: TemplateObject;
}>();

defineEmits<{
    (e: "select", v: TemplateObject): void;
}>();
</script>

<template>
    <UButton
        v-if="template.isButton"
        :color="template.object.is_blank ? 'hd-neutral' : 'white'"
        :variant="template.object.is_blank ? 'soft' : 'solid'"
        block
        class="items-start flex-col text-left p-4 text-sm rounded-lg"
        :class="template.isActive ? 'ring-2 ring-hd-secondary-500' : ''"
        @click.stop="$emit('select', template)"
    >
        <template v-if="template.banner">
            <nuxt-img
                :src="template.banner"
                class="absolute w-full h-full object-cover left-0 top-0 rounded-xl z-0 opacity-50"
            />
            <div
                class="absolute w-full h-full z-1 left-0 top- rounded-xl bg-gradient-to-b from-transparent to-white"
            ></div>
        </template>
        <template v-if="template.isGridImage">
            <div class="relative flex items-center space-x-3">
                <div class="shrink-0">
                    <UIAvatar
                        v-if="template.avatar"
                        :src="template.avatar"
                        size="lg"
                        class="rounded-full mr-1"
                    />
                </div>
                <div>
                    <div
                        v-if="
                            $slots['pre-badges'] ||
                            $slots['badges'] ||
                            $slots['post-badges'] ||
                            (template.badges ?? []).length
                        "
                        class="relative flex flex-wrap mb-2 gap-2 items-center"
                    >
                        <slot name="pre-badges" />
                        <slot :template="template" name="badges">
                            <template
                                v-for="(badge, idx) in template.badges ?? []"
                                :key="idx"
                            >
                                <UiBadge :badge="badge" class="h-6" />
                            </template>
                        </slot>
                        <slot name="post-badges" />
                    </div>
                    <slot name="pre-label" :template="template" />
                    <slot :template="template" name="label">
                        <div class="relative flex items-start font-medium">
                            <slot :template="template" name="label">
                                <span>{{ template.label }}</span>
                            </slot>
                        </div>
                    </slot>
                    <slot name="post-label" :template="template" />
                    <p
                        v-if="template.description"
                        class="relative mt-2 text-sm font-normal text-gray-500"
                    >
                        {{ template.description }}
                    </p>
                    <slot name="pre-cta" :template="template" />
                    <div
                        v-if="template.iframe || $slots['cta']"
                        class="relative mt-2 w-full"
                    >
                        <template
                            v-if="template.iframe && template.iframe.asWebFrame"
                        >
                            <HdResizer
                                v-slot="{ style }"
                                :class="template.iframe.class"
                                :scale="template.iframe.scale ?? 1"
                                class="[&>iframe]:pointer-events-none"
                            >
                                <MailWebFrame
                                    :document="template.iframe.src"
                                    :style="style"
                                    no-scroll
                                />
                            </HdResizer>
                        </template>
                        <hd-iframe
                            v-else-if="template.iframe"
                            class="pointer-events-none"
                            v-bind="template.iframe"
                        />
                        <slot :template="template" name="cta" />
                    </div>
                    <slot name="post-cta" :template="template" />
                </div>
            </div>
        </template>
        <template v-else>
            <div
                v-if="
                    $slots['pre-badges'] ||
                    $slots['badges'] ||
                    $slots['post-badges'] ||
                    (template.badges ?? []).length
                "
                class="relative flex flex-wrap mb-2 gap-2 items-center"
            >
                <slot name="pre-badges" />
                <slot :template="template" name="badges">
                    <template
                        v-for="(badge, idx) in template.badges ?? []"
                        :key="idx"
                    >
                        <UiBadge :badge="badge" class="h-6" />
                    </template>
                </slot>
                <slot name="post-badges" />
            </div>
            <slot name="pre-label" :template="template" />
            <slot :template="template" name="label">
                <div class="relative flex items-start font-medium">
                    <slot :template="template" name="label">
                        <UIAvatar
                            v-if="template.avatar"
                            :src="template.avatar"
                            class="rounded-full mr-1"
                        />
                        <span>{{ template.label }}</span>
                    </slot>
                </div>
            </slot>
            <slot name="post-label" :template="template" />
            <p
                v-if="template.description"
                class="relative mt-2 text-sm font-normal"
            >
                {{ template.description }}
            </p>
            <slot name="pre-cta" :template="template" />
            <div
                v-if="template.iframe || $slots['cta']"
                class="relative mt-2 w-full"
            >
                <template v-if="template.iframe && template.iframe.asWebFrame">
                    <HdResizer
                        v-slot="{ style }"
                        :class="template.iframe.class"
                        :scale="template.iframe.scale ?? 1"
                        class="[&>iframe]:pointer-events-none"
                    >
                        <MailWebFrame
                            :document="template.iframe.src"
                            :style="style"
                            no-scroll
                        />
                    </HdResizer>
                </template>
                <hd-iframe
                    v-else-if="template.iframe"
                    class="pointer-events-none"
                    v-bind="template.iframe"
                />
                <slot :template="template" name="cta" />
            </div>
            <slot name="post-cta" :template="template" />
        </template>
    </UButton>
    <div
        v-else
        class="items-start flex-col text-left p-4 text-sm border border-gray-300 rounded-lg shadow relative"
    >
        <template v-if="template.banner">
            <nuxt-img
                :src="template.banner"
                class="absolute w-full h-full object-cover left-0 top-0 rounded-xl z-0 opacity-50"
            />
            <div
                class="absolute w-full h-full z-1 left-0 top- rounded-xl bg-gradient-to-b from-transparent to-white"
            ></div>
        </template>
        <div class="relative flex flex-wrap mb-2 gap-2 z-10">
            <div class="flex-1 gap-2">
                <div class="flex flex-wrap items-center gap-2">
                    <slot name="pre-badges" />
                    <slot :template="template" name="badges">
                        <UBadge
                            v-for="badge in template.badges ?? []"
                            :key="badge"
                            :label="badge"
                            :ui="{ rounded: 'rounded-full' }"
                            class="capitalize h-6"
                            color="hd-neutral"
                            variant="soft"
                        />
                    </slot>
                    <slot name="post-badges" />
                </div>
            </div>
            <slot name="snapshot-taken" />
        </div>
        <slot name="pre-label" :template="template" />
        <div
            class="relative flex items-start font-medium z-10"
            v-if="$slots.label || template.label"
        >
            <slot :template="template" name="label">
                <span>{{ template.label }}</span>
            </slot>
        </div>
        <slot name="post-label" :template="template" />
        <p
            v-if="template.description"
            class="relative mt-2 text-sm font-normal z-10"
        >
            {{ template.description }}
        </p>
        <slot name="pre-cta" :template="template" />
        <div class="relative mt-2 w-full z-10">
            <template v-if="template.iframe && template.iframe.asWebFrame">
                <HdResizer
                    v-slot="{ style }"
                    :class="template.iframe.class"
                    :scale="template.iframe.scale ?? 1"
                    class="[&>iframe]:pointer-events-none"
                >
                    <MailWebFrame
                        :document="template.iframe.src"
                        :style="style"
                        no-scroll
                    />
                </HdResizer>
            </template>
            <hd-iframe
                v-else-if="template.iframe"
                class="pointer-events-none"
                v-bind="template.iframe"
            />
            <slot :template="template" name="cta" />
        </div>
        <slot name="post-cta" :template="template" />
    </div>
</template>
